import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import {
  UserPlus, ShoppingBag, CheckCircle2, ShieldCheck, Receipt,
  Activity, Database, Webhook, Shield, FileSearch,
} from "lucide-react";

const ICONS = {
  "user.signup": UserPlus,
  "order.placed": ShoppingBag,
  "invoice.paid": CheckCircle2,
  "insurance.applied": ShieldCheck,
  "insurance.claim": Receipt,
};

const TONES = {
  "user.signup": "bg-blue-50 text-blue-700",
  "order.placed": "bg-amber-50 text-amber-700",
  "invoice.paid": "bg-emerald-50 text-emerald-700",
  "insurance.applied": "bg-indigo-50 text-indigo-700",
  "insurance.claim": "bg-rose-50 text-rose-700",
};

// Admin platform activity log + system health snapshot.
export default function AdminActivity() {
  const [tab, setTab] = useState("activity"); // activity | webhooks | audit

  return (
    <>
      <PageHeader title="Platform Activity & Logs"
        subtitle="Real-time event log, webhook deliveries, and admin audit trail"/>
      <div className="p-8 space-y-4">
        <div className="flex gap-1.5 flex-wrap" data-testid="activity-tabs">
          {[
            { k: "activity",  l: "Activity feed", icon: Activity },
            { k: "webhooks",  l: "Webhook log",   icon: Webhook },
            { k: "audit",     l: "Audit trail",   icon: FileSearch },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm border ${tab === t.k ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/75 border-border hover:border-foreground/30"}`}>
              <t.icon className="w-3.5 h-3.5"/>{t.l}
            </button>
          ))}
        </div>

        {tab === "activity" && <ActivityTab/>}
        {tab === "webhooks" && <WebhookTab/>}
        {tab === "audit" && <AuditTab/>}
      </div>
    </>
  );
}

// ─── Activity feed (existing log + system health) ──────────────────────
function ActivityTab() {
  const [events, setEvents] = useState([]);
  const [health, setHealth] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/audit-log?limit=200").then(r => setEvents(r.data));
    api.get("/admin/system-health").then(r => setHealth(r.data));
  }, []);

  const filtered = filter === "all" ? events : events.filter(e => e.kind === filter);
  const kinds = [...new Set(events.map(e => e.kind))];

  return (
    <div className="grid lg:grid-cols-3 gap-6 items-start">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex gap-1.5 flex-wrap" data-testid="activity-filters">
          {[{ k: "all", l: "All", c: events.length }, ...kinds.map(k => ({
            k, l: k.replace(".", " · "), c: events.filter(e => e.kind === k).length,
          }))].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs border ${filter === f.k ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/75 border-border hover:border-foreground/30"}`}>
              {f.l}
              <span className={`text-[9px] mono ${filter === f.k ? "text-background/70" : "text-foreground/45"}`}>{f.c}</span>
            </button>
          ))}
        </div>
        <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="activity-feed">
          <div className="divide-y divide-border">
            {filtered.map((e, i) => {
              const Icon = ICONS[e.kind] || Activity;
              const tone = TONES[e.kind] || "bg-secondary text-foreground/70";
              return (
                <div key={i} className="px-5 py-3.5 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl ${tone} grid place-items-center shrink-0`}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{e.summary}</div>
                    <div className="text-[11px] text-foreground/50 mt-0.5">
                      <span className="mono uppercase tracking-widest">{e.kind}</span>
                      {" · "}<span className="text-foreground">{e.actor}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-foreground/55 shrink-0 mono">
                    {new Date(e.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="p-12 text-center text-sm text-foreground/55">No events</div>}
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <div className="rounded-2xl bg-white border border-border p-6" data-testid="health-card">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-foreground/50"/>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Database</div>
          </div>
          <div className="display text-3xl mt-2">{health?.total_documents?.toLocaleString() || "…"}</div>
          <div className="text-xs text-foreground/55 mt-1">total documents · {health?.uptime}</div>
          {health && (
            <div className="mt-5 space-y-1.5">
              {Object.entries(health.db_collections)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 12)
                .map(([col, n]) => (
                  <div key={col} className="flex items-center justify-between text-[12px]">
                    <span className="text-foreground/65 capitalize">{col.replace(/_/g, ' ')}</span>
                    <span className="mono text-foreground">{n}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Webhook log (read-only) ───────────────────────────────────────────
function WebhookTab() {
  const [data, setData] = useState({ events: [], sources: [] });
  const [src, setSrc] = useState("");

  const load = (s) => api.get(`/admin/webhook-log?limit=200${s ? `&source=${s}` : ""}`).then(r => setData(r.data));
  useEffect(() => { load(src); }, [src]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5 items-center">
        <button onClick={() => setSrc("")} className={`text-xs px-3 py-1 rounded-full border ${!src ? "bg-foreground text-background border-foreground" : "border-border bg-white"}`} data-testid="src-all">All sources</button>
        {data.sources.map(s => (
          <button key={s} onClick={() => setSrc(s)} className={`text-xs px-3 py-1 rounded-full border ${src === s ? "bg-foreground text-background border-foreground" : "border-border bg-white"}`} data-testid={`src-${s}`}>
            {s}
          </button>
        ))}
        <span className="text-[11px] text-foreground/50 ml-2">{data.events.length} events</span>
      </div>
      <div className="rounded-2xl bg-white border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-foreground/5 text-[11px] uppercase tracking-widest text-foreground/55">
            <tr>
              <th className="text-left px-4 py-2">Source</th>
              <th className="text-left px-4 py-2">Event</th>
              <th className="text-left px-4 py-2">Received</th>
              <th className="text-left px-4 py-2">Reference</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.events.map((e, i) => (
              <tr key={i} className="hover:bg-foreground/[0.02]">
                <td className="px-4 py-2.5"><span className="text-[10px] uppercase tracking-wide bg-foreground/10 px-2 py-0.5 rounded">{e._source}</span></td>
                <td className="px-4 py-2.5 mono text-[12px]">{e.event_type || e.type || e.kind || "—"}</td>
                <td className="px-4 py-2.5 text-foreground/65 text-[11px] mono">{e.received_at || e.created_at || "—"}</td>
                <td className="px-4 py-2.5 text-[11px] mono text-foreground/55 truncate max-w-[280px]">{e.payload?.id || e.shipment_id || e.order_id || e.reference || ""}</td>
              </tr>
            ))}
            {data.events.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-foreground/55 text-sm">No webhook events yet for this source.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Admin audit trail (write-action log) ──────────────────────────────
function AuditTab() {
  const [events, setEvents] = useState([]);
  const [actor, setActor] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const load = () => {
    const qs = new URLSearchParams();
    if (actor) qs.set("actor_id", actor);
    if (actionFilter) qs.set("action", actionFilter);
    qs.set("limit", "200");
    api.get(`/admin/audit-trail?${qs.toString()}`).then(r => setEvents(r.data.events || []));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-foreground/50">Actor user ID</label>
          <input value={actor} onChange={e => setActor(e.target.value)}
            placeholder="(any actor)" className="block h-8 px-2 text-xs rounded border border-border w-64"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-foreground/50">Action contains</label>
          <input value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            placeholder="e.g. claim.force_resolve" className="block h-8 px-2 text-xs rounded border border-border w-64"/>
        </div>
        <button onClick={load} className="h-8 px-3 text-xs rounded-full bg-foreground text-background" data-testid="audit-refresh">
          Refresh
        </button>
      </div>
      <div className="rounded-2xl bg-white border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-foreground/5 text-[11px] uppercase tracking-widest text-foreground/55">
            <tr>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Actor</th>
              <th className="text-left px-4 py-2">Action</th>
              <th className="text-left px-4 py-2">Target</th>
              <th className="text-left px-4 py-2">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {events.map((e, i) => (
              <tr key={i} className="hover:bg-foreground/[0.02]">
                <td className="px-4 py-2.5 text-[11px] mono text-foreground/65">{e.at || ""}</td>
                <td className="px-4 py-2.5 text-[11px] mono">{e.actor_email || e.actor_id || "system"}</td>
                <td className="px-4 py-2.5"><span className="text-[10px] uppercase tracking-wide bg-foreground/10 px-2 py-0.5 rounded mono">{e.action || "—"}</span></td>
                <td className="px-4 py-2.5 text-[11px] mono text-foreground/55 truncate max-w-[200px]">{e.target_id || e.target || ""}</td>
                <td className="px-4 py-2.5 text-[12px] text-foreground/75 truncate max-w-[280px]">{e.note || JSON.stringify(e.changes || {}).slice(0, 80) || ""}</td>
              </tr>
            ))}
            {events.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-foreground/55 text-sm">No audit events match this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
